/**
 * Complaint-Repairs Silo Breaker (F-090) — product walkthrough.
 *
 * Reads live data only from GET /api/silo-links + /api/silo-links/board +
 * /api/silo-links/:linkId/timeline + /api/silo-links/:linkId/evidence-safe.
 * No fixtures/mock data — if a tenant has no linked cases yet, that's shown
 * honestly as an empty state rather than faked.
 *
 * This is the page the RSL Email 5 "See how case linking works" CTA points
 * to, and doubles as the real walkthrough for how the auto-link rule, the
 * shared timeline, the sealed Evidence Safe bundle, and the cross-team
 * handover gate actually behave once repairs + complaints are registered.
 */
import React, { useEffect, useState, useCallback } from 'react';
import Navbar from '../Dashboard/components/Navbar';
import Footer from '../Dashboard/components/Footer';
import { siloBreakerService } from '../../services/api';

interface CaseRef {
    caseId: string;
    caseRef: string;
    status: string;
}

interface Owner {
    name: string;
    team: string;
}

interface Acknowledgement {
    owner: 'complaint' | 'repairs';
    by: string;
    at: string;
}

interface SiloLink {
    linkId: string;
    tenantId: string | null;
    propertyId: string | null;
    autoLinked: boolean;
    rule: any;
    repairsCase: CaseRef | null;
    complaintCase: CaseRef | null;
    owners: { complaint: Owner; repairs: Owner } | null;
    handover: { required: boolean; acknowledgements: Acknowledgement[] };
    seals: { sealId: string; sealedAt: string }[];
    timeline: { at: string; action: string; details: string; by: string }[];
    linkedAt: string;
    gate: { canClose: boolean; blockedReasons: string[] };
}

interface BoardMetric {
    totalComplaints: number;
    totalInFlightRepairs: number;
    complaintsLinked?: number;
    complaintsLinkedPct?: number;
    repairsLinked?: number;
    repairsLinkedPct?: number;
}

function StatusPill({ status }: { status: string }) {
    const s = (status || '').toUpperCase();
    const styles: Record<string, string> = {
        OPEN: 'bg-amber-100 text-amber-800 border-amber-200',
        CLOSED: 'bg-slate-100 text-slate-600 border-slate-200',
        COMPLETED: 'bg-slate-100 text-slate-600 border-slate-200',
    };
    return (
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${styles[s] || 'bg-blue-100 text-blue-800 border-blue-200'}`}>
            {status || 'UNKNOWN'}
        </span>
    );
}

export default function SiloBreakerPage() {
    const [links, setLinks] = useState<SiloLink[]>([]);
    const [metric, setMetric] = useState<BoardMetric | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [selected, setSelected] = useState<SiloLink | null>(null);
    const [timeline, setTimeline] = useState<any[] | null>(null);
    const [seals, setSeals] = useState<any[] | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [actionMessage, setActionMessage] = useState<string | null>(null);
    const [actionBusy, setActionBusy] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.allSettled([
            siloBreakerService.listLinks(),
            siloBreakerService.getBoardMetric(),
        ])
            .then(([linksResult, metricResult]) => {
                if (linksResult.status === 'fulfilled') {
                    setLinks((linksResult.value as any).data?.data?.links || []);
                }
                // Intentionally silent on failure here — falls back to the
                // metric-derived summary card and the list section's own
                // empty state rather than surfacing a scary error banner
                // for what's just one section being unavailable.
                if (metricResult.status === 'fulfilled') {
                    setMetric((metricResult.value as any).data?.data || null);
                }
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const openDetail = (link: SiloLink) => {
        setSelected(link);
        setTimeline(null);
        setSeals(null);
        setActionMessage(null);
        setDetailLoading(true);
        Promise.all([
            siloBreakerService.getTimeline(link.linkId),
            siloBreakerService.getEvidenceSafe(link.linkId),
        ])
            .then(([tRes, eRes]: any[]) => {
                setTimeline(tRes.data?.data?.timeline || []);
                setSeals(eRes.data?.data?.seals || []);
            })
            .catch((e: any) => setActionMessage(e?.response?.data?.message || 'Failed to load link detail'))
            .finally(() => setDetailLoading(false));
    };

    const refreshSelected = (linkId: string) => {
        siloBreakerService.getLink(linkId).then((res: any) => {
            const updated = res.data?.data;
            if (updated) setSelected(updated);
        });
        load();
    };

    const handleAcknowledge = async (owner: 'complaint' | 'repairs') => {
        if (!selected) return;
        setActionBusy(true);
        setActionMessage(null);
        try {
            await siloBreakerService.acknowledgeHandover(selected.linkId, owner);
            setActionMessage(`${owner === 'complaint' ? 'Complaints' : 'Repairs'} owner acknowledged handover.`);
            refreshSelected(selected.linkId);
        } catch (e: any) {
            setActionMessage(e?.response?.data?.message || 'Failed to acknowledge handover');
        } finally {
            setActionBusy(false);
        }
    };

    const handleSeal = async () => {
        if (!selected) return;
        setActionBusy(true);
        setActionMessage(null);
        try {
            await siloBreakerService.sealBundle(selected.linkId);
            setActionMessage('Combined Evidence Safe bundle sealed.');
            openDetail(selected);
        } catch (e: any) {
            setActionMessage(e?.response?.data?.message || 'Failed to seal bundle');
        } finally {
            setActionBusy(false);
        }
    };

    const handleCloseCheck = async () => {
        if (!selected) return;
        setActionBusy(true);
        setActionMessage(null);
        try {
            const res: any = await siloBreakerService.closeCheck(selected.linkId);
            setActionMessage('Both cases closed together — handover complete.');
            refreshSelected(res.data?.data?.link?.linkId || selected.linkId);
        } catch (e: any) {
            setActionMessage(e?.response?.data?.message || e?.response?.data?.data?.blockedReasons?.join('; ') || 'Close blocked by handover protocol');
        } finally {
            setActionBusy(false);
        }
    };

    const pctLinked = metric ? Math.max(metric.complaintsLinkedPct ?? 0, metric.repairsLinkedPct ?? 0) : 0;
    // Fall back to a distinct-complaint count, not links.length: F-058 lets
    // multiple hazards link to the same complaint, so raw link-record count
    // can overcount "cases linked" versus the true number of complaints
    // involved (metric.complaintsLinked, when available, is the source of
    // truth and always preferred).
    const casesLinkedCount = metric?.complaintsLinked ??
        new Set(links.map((l) => l.complaintCase?.caseId).filter(Boolean)).size;

    return (
        <div className="min-h-screen bg-slate-50">
            <Navbar />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-2 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">
                            <i className="fas fa-link text-emerald-600 mr-2"></i>Complaint-Repairs Silo Breaker
                        </h1>
                        <p className="text-slate-500 mt-1 max-w-3xl">
                            A repair goes quiet, the tenant escalates to a complaint, and the two teams never talk. This links
                            them automatically on SLA breach + repeated tenant contact — one shared timeline, one sealed
                            Evidence Safe bundle, one cross-team handover gate before either case can close.
                        </p>
                    </div>
                    <button
                        onClick={load}
                        className="shrink-0 inline-flex items-center gap-2 bg-white hover:bg-slate-50 text-emerald-700 font-semibold px-4 py-2.5 rounded-xl shadow-sm border border-emerald-200 transition-colors"
                    >
                        <i className="fas fa-rotate"></i> Refresh
                    </button>
                </div>

                {error && (
                    <div className="mt-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
                        {error}
                    </div>
                )}

                {/* Board metric */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Complaints (total)</div>
                        <div className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : metric?.totalComplaints ?? 0}</div>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">In-flight repairs</div>
                        <div className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : metric?.totalInFlightRepairs ?? 0}</div>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Cases linked</div>
                        <div className="text-3xl font-bold text-slate-900 mt-1">{loading ? '—' : casesLinkedCount}</div>
                    </div>
                    <div className="bg-emerald-600 rounded-2xl shadow-sm p-5 text-white">
                        <div className="text-xs font-semibold text-emerald-100 uppercase tracking-wide">Silo-break metric</div>
                        <div className="text-3xl font-bold mt-1">{loading ? '—' : `${Math.round(pctLinked)}%`}</div>
                        <div className="text-xs text-emerald-100 mt-1">of in-flight complaints/repairs already linked</div>
                    </div>
                </div>

                {/* Links list */}
                <div className="mt-8 bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="px-5 py-4 border-b border-slate-100">
                        <h3 className="text-base font-bold text-slate-900">Linked cases</h3>
                        <p className="text-sm text-slate-500 mt-0.5">Every repair-complaint pair the silo breaker has linked, newest first.</p>
                    </div>
                    {loading ? (
                        <div className="text-sm text-slate-400 py-8 text-center">Loading linked cases…</div>
                    ) : links.length === 0 ? (
                        <div className="text-sm text-slate-500 py-10 text-center px-6">
                            <i className="fas fa-circle-check text-emerald-500 text-2xl mb-2 block"></i>
                            No cases linked yet. As soon as a repair breaches its SLA with repeated tenant contact, or a case is
                            manually linked, it will appear here with a shared timeline and sealed evidence bundle.
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {links.map((link) => (
                                <button
                                    key={link.linkId}
                                    onClick={() => openDetail(link)}
                                    className="w-full text-left px-5 py-4 hover:bg-slate-50 transition-colors flex items-center justify-between gap-4"
                                >
                                    <div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-semibold text-slate-900">{link.repairsCase?.caseRef || link.repairsCase?.caseId}</span>
                                            <i className="fas fa-arrow-right-arrow-left text-slate-300 text-xs"></i>
                                            <span className="text-sm font-semibold text-slate-900">{link.complaintCase?.caseRef || link.complaintCase?.caseId}</span>
                                            {link.autoLinked && (
                                                <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Auto-linked</span>
                                            )}
                                        </div>
                                        <div className="text-xs text-slate-400 mt-1">
                                            Linked {link.linkedAt ? new Date(link.linkedAt).toLocaleString('en-GB') : '—'} · {link.owners?.repairs?.team || 'Repairs'} + {link.owners?.complaint?.team || 'Complaints'}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <StatusPill status={link.repairsCase?.status || ''} />
                                        <StatusPill status={link.complaintCase?.status || ''} />
                                        {link.gate?.canClose ? (
                                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Ready to close</span>
                                        ) : (
                                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">Handover pending</span>
                                        )}
                                        <i className="fas fa-chevron-right text-slate-300"></i>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Detail panel */}
            {selected && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setSelected(null)}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-lg font-bold text-slate-900">
                                    {selected.repairsCase?.caseRef} <i className="fas fa-arrow-right-arrow-left text-slate-300 text-sm mx-1"></i> {selected.complaintCase?.caseRef}
                                </h3>
                                <p className="text-xs text-slate-400 mt-1">Link ID {selected.linkId}</p>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-600">
                                <i className="fas fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="px-6 py-4 space-y-6">
                            {actionMessage && (
                                <div className="bg-slate-50 border border-slate-200 text-slate-700 text-sm rounded-xl px-4 py-2.5">{actionMessage}</div>
                            )}

                            {/* Owners + handover gate */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <div className="text-xs font-semibold text-slate-400 uppercase">Repairs owner</div>
                                    <div className="text-sm font-semibold text-slate-900 mt-1">{selected.owners?.repairs?.name} ({selected.owners?.repairs?.team})</div>
                                    <button
                                        disabled={actionBusy}
                                        onClick={() => handleAcknowledge('repairs')}
                                        className="mt-3 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 disabled:opacity-50"
                                    >
                                        Acknowledge handover
                                    </button>
                                </div>
                                <div className="border border-slate-200 rounded-xl p-4">
                                    <div className="text-xs font-semibold text-slate-400 uppercase">Complaints owner</div>
                                    <div className="text-sm font-semibold text-slate-900 mt-1">{selected.owners?.complaint?.name} ({selected.owners?.complaint?.team})</div>
                                    <button
                                        disabled={actionBusy}
                                        onClick={() => handleAcknowledge('complaint')}
                                        className="mt-3 text-xs font-semibold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg px-3 py-1.5 disabled:opacity-50"
                                    >
                                        Acknowledge handover
                                    </button>
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <button disabled={actionBusy} onClick={handleSeal} className="text-xs font-semibold text-slate-700 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 disabled:opacity-50">
                                    <i className="fas fa-lock mr-1"></i> Seal Evidence Safe bundle
                                </button>
                                <button disabled={actionBusy || !selected.gate?.canClose} onClick={handleCloseCheck} className="text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5 disabled:opacity-50">
                                    <i className="fas fa-circle-check mr-1"></i> Close both cases together
                                </button>
                            </div>
                            {!selected.gate?.canClose && selected.gate?.blockedReasons?.length > 0 && (
                                <div className="text-xs text-amber-700 -mt-3">Blocked: {selected.gate.blockedReasons.join('; ')}</div>
                            )}

                            {/* Shared timeline */}
                            <div>
                                <h4 className="text-sm font-bold text-slate-900 mb-2">Shared timeline</h4>
                                {detailLoading ? (
                                    <div className="text-sm text-slate-400">Loading…</div>
                                ) : (timeline?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-slate-400">No timeline entries yet.</div>
                                ) : (
                                    <ul className="space-y-2">
                                        {timeline!.map((t: any, i: number) => (
                                            <li key={i} className="text-sm border-l-2 border-emerald-200 pl-3">
                                                <span className="text-slate-400 text-xs">{t.at ? new Date(t.at).toLocaleString('en-GB') : ''}</span>
                                                <div className="text-slate-800">{t.details || t.action}</div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* Evidence Safe seals */}
                            <div>
                                <h4 className="text-sm font-bold text-slate-900 mb-2">Evidence Safe bundles</h4>
                                {detailLoading ? (
                                    <div className="text-sm text-slate-400">Loading…</div>
                                ) : (seals?.length ?? 0) === 0 ? (
                                    <div className="text-sm text-slate-400">No sealed bundles yet — seal one above.</div>
                                ) : (
                                    <ul className="space-y-1">
                                        {seals!.map((s: any) => (
                                            <li key={s.sealId} className="text-sm text-slate-700 flex items-center gap-2">
                                                <i className={`fas ${s.verified ? 'fa-shield-halved text-emerald-500' : 'fa-triangle-exclamation text-amber-500'}`}></i>
                                                Sealed {s.sealedAt ? new Date(s.sealedAt).toLocaleString('en-GB') : ''} {s.verified ? '· integrity verified' : '· integrity check failed'}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
